import { t } from "@lingui/macro";
import {
  DistributionModalFilterOptionsDocument,
  type DistributionModalFilterOptionsQuery,
  type DistributionModalFilterOptionsQueryVariables,
  DistributionModalTableDataDocument,
  type DistributionModalTableDataQuery,
  type DistributionModalTableDataQueryVariables,
  type DistributionModalTableItemFragment,
  type GetOurWorkBudgetItemsSimpleMapOptionsQuery,
  GetTasksSimpleMapOptionsDocument,
  type GetTasksSimpleMapOptionsQuery,
  type GetTasksSimpleMapOptionsQueryVariables,
  SortOrder,
  SqlOperator,
  TimeTrackingItemOrderByColumnEnum,
  TimeTrackingReportsWhereColumn,
  UserScopeEnum,
  UsersWhereColumn,
} from "@src/__generated__/graphql";
import { BulkActionsStore } from "@src/components/widgets/bulk-actions/store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { toApiDate } from "@src/utils/dates";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange } from "@src/utils/types";
import { isSameDay } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import { isUnsavedId } from "../../store";

type DisclosureStateData = {
  allowSelect: boolean;
  dateFrom: string;
  dateTo: string;
  initialSelectedTotalAmount: number;
  initialSelectedTimeTrackingItemIds: string[];
  onConfirm: (itemIds: string[]) => void;
  timeBillingPlanId: string;
};

const SUBTASK_FILTER_COLUMN = "SUBTASK_ID" as TimeTrackingReportsWhereColumn;

export class DistributionsModalStore {
  readonly projectId: string;

  readonly bulkActionsStore = new BulkActionsStore([], undefined, (ids) =>
    this.setSelectedTimeTrackingItemIds(ids),
  );

  /** Filters for fetching time tracking items */
  readonly where = new Filters<TimeTrackingReportsWhereColumn>(
    [
      // ProjectId filter - always required
      new Filter({
        column: TimeTrackingReportsWhereColumn.ProjectId,
        operator: SqlOperator.Eq,
        options: [],
        value: "", // Will be set in constructor
        hidden: true,
      }),
      // For existing time billing: filter by plan ID
      new Filter({
        column: TimeTrackingReportsWhereColumn.ProjectTimeBillingPlanId,
        operator: SqlOperator.Eq,
        options: [],
        value: "", // Will be updated onOpen
        hidden: true,
      }),
      new Filter({
        column: TimeTrackingReportsWhereColumn.HasTimeBilling,
        operator: SqlOperator.Eq,
        options: [],
        value: ["false"],
        hidden: true,
      }),
      new Filter({
        title: t`Budget items`,
        column: TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
        operator: SqlOperator.In,
        noOptionsMessage: t`Please select Project first`,
        options: [],
        onChange: () => {
          this.fetchTasksOptions();
        },
      }),
      new Filter({
        title: t`Tasks`,
        noOptionsMessage: t`Please select Budget item first`,
        column: TimeTrackingReportsWhereColumn.TaskId,
        operator: SqlOperator.In,
        options: [],
        onChange: () => {
          this.fetchSubtasksOptions();
        },
        mutateToGql: (value) => {
          return this.selectedSubtaskIds.length
            ? this.selectedSubtaskIds
            : value;
        },
      }),
      new Filter({
        title: t`Subtasks`,
        noOptionsMessage: t`Please select Task first`,
        column: SUBTASK_FILTER_COLUMN,
        operator: SqlOperator.In,
        includeInGql: false,
        onChange: (value) => {
          this.setSelectedSubtaskIds(value ?? []);
        },
        options: [],
      }),
      new Filter({
        title: t`Users`,
        column: TimeTrackingReportsWhereColumn.UserId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Positions`,
        column: TimeTrackingReportsWhereColumn.WorkTypeId,
        operator: SqlOperator.In,
        options: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
      },
    },
  );

  private userSimpleWhere = new Filters<UsersWhereColumn>([
    new Filter({
      column: UsersWhereColumn.Scope,
      operator: SqlOperator.Eq,
      value: UserScopeEnum.Internal,
      options: [],
    }),
  ]);

  @observable.ref
  tableData: DistributionModalTableItemFragment[] = [];

  @observable
  selectedTotalAmount = 0;

  @observable.ref
  selectedSubtaskIds: string[] = [];

  pagination = new PaginationState("distributions-modal", {
    perPageOptions: [25, 50, 100, 250, 500],
    onChangePagination: () => {
      if (!this.disclosureState.isOpen) return;
      this.fetchDistributions();
    },
  });

  @observable
  dateRange: DateRange = { start: new Date(), end: new Date() };

  orderBy = new OrderBy<TimeTrackingItemOrderByColumnEnum>([
    {
      column: TimeTrackingItemOrderByColumnEnum.Date,
      order: SortOrder.Desc,
    },
  ]);

  @observable.ref
  optionsData?: {
    timeTrackingWorkTypes?: DistributionModalFilterOptionsQuery["timeTrackingWorkTypes"];
    userSimpleMap?: DistributionModalFilterOptionsQuery["userSimpleMap"];
    tasksSimpleMap?: GetTasksSimpleMapOptionsQuery["tasksSimpleMap"];
    subtasksSimpleMap?: GetTasksSimpleMapOptionsQuery["tasksSimpleMap"];
    ourWorkBudgetItemsSimpleMap?: GetOurWorkBudgetItemsSimpleMapOptionsQuery["ourWorkBudgetItemsSimpleMap"];
  };

  /** Map to store ID -> amount for calculating sum of selected items across pages */
  @observable
  itemsAmountMap = new Map<string, number>();

  private distributionsFetcher = new FetchHelper<
    DistributionModalTableDataQuery,
    DistributionModalTableDataQueryVariables
  >(DistributionModalTableDataDocument);

  private filterOptionsFetcher = new FetchHelper<
    DistributionModalFilterOptionsQuery,
    DistributionModalFilterOptionsQueryVariables
  >(DistributionModalFilterOptionsDocument);

  private tasksOptionsFetcher = new FetchHelper<
    GetTasksSimpleMapOptionsQuery,
    GetTasksSimpleMapOptionsQueryVariables
  >(GetTasksSimpleMapOptionsDocument);

  readonly disclosureState = new DisclosureState<DisclosureStateData>({
    onOpen: (data) => {
      if (!data) return;
      this.bulkActionsStore.setSelectedIds(
        data.initialSelectedTimeTrackingItemIds,
      );
      data.initialSelectedTimeTrackingItemIds;
      this.selectedTotalAmount = data.initialSelectedTotalAmount;

      if (
        isSameDay(this.dateRange.start, new Date()) &&
        isSameDay(this.dateRange.end, new Date())
      ) {
        this.dateRange = {
          start: new Date(data.dateFrom ?? new Date()),
          end: new Date(data.dateTo ?? new Date()),
        };
      }

      this.itemsAmountMap.clear();

      // Update filters based on whether editing existing or creating new
      const isEditing = !isUnsavedId(data.timeBillingPlanId);

      const planIdFilter = this.where.filtersByColumn.get(
        TimeTrackingReportsWhereColumn.ProjectTimeBillingPlanId,
      );
      const hasTimeBilling = this.where.filtersByColumn.get(
        TimeTrackingReportsWhereColumn.HasTimeBilling,
      );

      if (isEditing) {
        // Editing existing: set plan ID, clear billing status
        planIdFilter?.setValue(data.timeBillingPlanId);
        hasTimeBilling?.setValue([]);
      } else {
        // Creating new: clear plan ID, set billing status to Uninvoiced
        planIdFilter?.setValue([]);
        hasTimeBilling?.setValue(["false"]);
      }

      this.fetchDistributions();
    },
    onClose: () => {
      this.bulkActionsStore.setSelectedIds([]);
      this.selectedTotalAmount = 0;
      this.tableData = [];
      this.itemsAmountMap.clear();
      this.pagination.resetPage();
    },
  });

  constructor(projectId: string) {
    makeObservable(this);
    this.projectId = projectId;
    // Set ProjectId filter value
    const projectIdFilter = this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.ProjectId,
    );
    projectIdFilter?.setValue(projectId);

    this.fetchFilterOptions(projectId);
  }

  @computed
  get isLoading(): boolean {
    return this.distributionsFetcher.isLoading.value;
  }

  @computed
  get allowSelect(): boolean {
    return !!this.disclosureState.additionalData?.allowSelect;
  }

  @computed
  get modalTitle(): string {
    return t`Time Tracking Items`;
  }

  @computed
  private get owbiFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
    );
  }

  @computed
  private get taskFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.TaskId,
    );
  }

  @computed
  private get subtaskFilter():
    | Filter<TimeTrackingReportsWhereColumn>
    | undefined {
    return this.where.filtersByColumn.get(SUBTASK_FILTER_COLUMN);
  }

  @computed
  private get workTypeFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.WorkTypeId,
    );
  }

  @computed
  private get userFilter() {
    return this.where.filtersByColumn.get(
      TimeTrackingReportsWhereColumn.UserId,
    );
  }

  @action.bound
  setDateRange(value: DateRange): void {
    this.dateRange = value;
    this.pagination.resetPage();
  }

  @action.bound
  async fetchDistributions(): Promise<void> {
    const [data] = await this.distributionsFetcher.fetch({
      from: toApiDate(this.dateRange.start),
      to: toApiDate(this.dateRange.end),
      where: this.where.asGraphQueryParam,
      first: this.pagination.asParams.first,
      page: this.pagination.asParams.page,
      orderBy: this.orderBy.asWhereParam,
    });

    if (!data?.getTimeTrackingItems) return;

    data.getTimeTrackingItems.data.forEach((item) => {
      this.itemsAmountMap.set(item.id, item.amount ?? 0);
    });

    this.tableData = this.allowSelect
      ? data.getTimeTrackingItems.data
      : data.getTimeTrackingItems.data.filter((item) =>
          this.bulkActionsStore.selectedIds.has(item.id),
        );
    this.pagination.setFromPaginatorInfo(
      data.getTimeTrackingItems.paginatorInfo,
    );
  }

  @action.bound
  setSelectedTimeTrackingItemIds(ids: string[]): void {
    this.updateSelectedTotalAmount(this.bulkActionsStore.selectedIdsArray, ids);
    this.bulkActionsStore.setSelectedIds(ids);
    if (ids.length > 0) return;
    this.selectedTotalAmount = 0;
  }

  @action.bound
  private updateSelectedTotalAmount(
    currentIds: string[],
    nextIds: string[],
  ): void {
    const currentSet = new Set(currentIds);
    const nextSet = new Set(nextIds);

    let delta = 0;
    nextSet.forEach((id) => {
      if (!currentSet.has(id)) {
        delta += this.itemsAmountMap.get(id) ?? 0;
      }
    });
    currentSet.forEach((id) => {
      if (!nextSet.has(id)) {
        delta -= this.itemsAmountMap.get(id) ?? 0;
      }
    });

    this.selectedTotalAmount += delta;
  }

  @action.bound
  handleOrderChange(orderBy: {
    column: TimeTrackingItemOrderByColumnEnum;
    order: SortOrder | undefined;
  }): void {
    if (!orderBy.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: orderBy.column, order: orderBy.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  handleConfirm(): void {
    this.disclosureState.additionalData?.onConfirm?.(
      this.bulkActionsStore.selectedIdsArray,
    );
    this.disclosureState.onClose();
  }

  @action.bound
  private async fetchFilterOptions(projectId: string) {
    const [data, error] = await this.filterOptionsFetcher.fetch({
      projectId,
      userSimpleWhere: this.userSimpleWhere.asWhereParam,
    });

    if (error || !data) return;

    this.optionsData = data;

    this.userFilter?.setOptions(mapToOptions.users(data.userSimpleMap));
    this.owbiFilter?.setOptions(
      mapToOptions.toOptions(data.ourWorkBudgetItemsSimpleMap, "title"),
    );
    this.workTypeFilter?.setOptions(
      mapToOptions.toOptions(data.timeTrackingWorkTypes, "title"),
    );

    this.owbiFilter?.sanitizeAvailableValue({ skipOnChange: true });
    this.taskFilter?.sanitizeAvailableValue({ skipOnChange: true });
    this.filterUserOptions();
    this.workTypeFilter?.sanitizeAvailableValue();
  }

  @action.bound
  private async fetchTasksOptions() {
    const [data, error] = await this.tasksOptionsFetcher.fetch({
      our_work_budget_item_id: this.owbiFilter?.value ?? [],
    });

    if (!data?.tasksSimpleMap || error) return;
    const parentTasks = data.tasksSimpleMap.filter((task) => !task.parent);
    if (this.optionsData) {
      this.optionsData.tasksSimpleMap = parentTasks;
    }
    this.taskFilter?.setOptions(mapToOptions.toOptions(parentTasks, "name"));
    this.taskFilter?.sanitizeAvailableValue();
    await this.fetchSubtasksOptions();
  }

  @action.bound
  private setSelectedSubtaskIds(value: string[]) {
    this.selectedSubtaskIds = Array.from(new Set(value));
  }

  @action.bound
  private async fetchSubtasksOptions() {
    const [data, error] = await this.tasksOptionsFetcher.fetch({
      parent_ids: this.taskFilter?.value ?? [],
      our_work_budget_item_id: this.owbiFilter?.value ?? [],
    });

    if (!data?.tasksSimpleMap || error) return;
    if (this.optionsData) {
      this.optionsData.subtasksSimpleMap = data.tasksSimpleMap;
    }
    this.subtaskFilter?.setOptions(
      mapToOptions.toOptions(data.tasksSimpleMap, "name"),
    );
    this.subtaskFilter?.sanitizeAvailableValue();
  }

  private filterUserOptions() {
    const filteredOptions = this.optionsData?.userSimpleMap ?? [];

    this.userFilter?.setOptions([
      ...mapToOptions.users(filteredOptions),
      {
        label: t`Work expense`,
        value: "work-expense",
      },
    ]);
    this.userFilter?.sanitizeAvailableValue({ skipOnChange: true });
  }
}
